import { Injectable, Renderer2, RendererFactory2, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { DomSanitizer, Meta, SafeHtml, Title } from '@angular/platform-browser';
import { seoContent } from './seoContent';

/**
 * Seo Service
 * @export
 * @class SeoService
 */
@Injectable({
  providedIn: 'root'
})
export class SeoService {
  private renderer: Renderer2;

  /**
   * Creates an instance of SeoService.
   * @param {Meta} meta
   * @param {Title} title
   * @param {RendererFactory2} rendererFactory
   * @param {Object} platformId
   * @param {DomSanitizer} sanitizer
   */
  constructor(
    private meta: Meta,
    private title: Title,
    rendererFactory: RendererFactory2,
    @Inject(PLATFORM_ID) private platformId: Object,
    private sanitizer: DomSanitizer) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  /**
   * This method is used to add title, meta and link tags to a page
   * @param {string} url
   * @param {string} title
   * @param {string} description
   */
  addTitleAndTags(url: string, title: string, description: string) {

    this.title.setTitle(title);

    this.meta.addTags([
      { name: 'description', content: description }
    ]);

    if (isPlatformBrowser(this.platformId)) {
      const link = this.renderer.createElement('link');
      this.renderer.setAttribute(link, 'rel', "canonical");
      this.renderer.setAttribute(link, 'href', url);
      this.renderer.appendChild(document.head, link);
    }

  }

  /**
   * This method is used to add the structure data to the pages
   * @param {string} url
   * @param {string} breadCrumbName
   */
  addStructuredData(url: string, breadCrumbName: string) {

    const logo = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "SP Software (P) Limited",
      "url": url,
      "logo": "https://www.spsoftglobal.com/assets/images/spsoft.svg"
    }

    const breadCrumb = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "SP Software (P) Limited",
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "item": {
              "@id": `${url}/#breadcrumb`,
              "name": breadCrumbName
            }
          }
        ]
      }
    }

    const logoData = this.getSafeHTML(logo);
    const breadData = this.getSafeHTML(breadCrumb);
    return { logoData, breadData };

  }

  /**
   * This method is used to return the script tag in application LD-JSON format
   * @param {{ [key: string]: any }} jsonLD
   * @return {*}  {SafeHtml}
   */
  getSafeHTML(jsonLD: { [key: string]: any }): SafeHtml {
    const json = jsonLD ? JSON.stringify(jsonLD, null, 2).replace(/<\/script>/g, '<\\/script>') : '';
    // escape / to prevent script tag in JSON
    const html = `<script type="application/ld+json">${json}</script>`;
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  /**
   * This method is used to add structure data to the home page
   */
  addLocalBusinessStructureData() {
    const localBusinessData = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "SP Software (P) Limited",
      "image": `${seoContent.main.url}/assets/images/spsoft.svg`,
      "@id": seoContent.main.url,
      "url": seoContent.main.url,
      "logo": `${seoContent.main.url}/assets/images/spsoft.svg`,
      "description": "SP Software (P) Limited, a flagship of SP Group, is a leading enterprise business Software and Information Technology (IT) Services company, providing IT Consulting, Technology and Outsourcing Services. Established in 1995, SPSoft is one of the fastest growing IT companies in India having a strong strategic alliance with leading global companies and has the capability to handle orders of any size. We offer varied services like Software Development, AI & ML Solutions, Data Analytics, Mobile Application Development, Web Application Development, SEO and Digital Marketing, Testing Services, and Workforce Solutions.",
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+91 (40) 4448 1155",
        "mail": "info@spsoftglobal.com",
        "availableLanguage": "English",
        "openingHours": "Mon – Fri 09:00-18:00",
        "website": seoContent.main.url
      },
      "founder": {
        "@type": "Person",
        "name": "S.Pulla Reddy",
        "description": "Founder and CEO of SP Software (P) Limited"
      },
      "brand": {
        "@type": "Brand",
        "name": "SP Software (P) Limited",
        "foundingDate": "1995-06-05",
        "numberOfEmployees": "700 - 900",
        "slogan": "Your Digital Transformation Partner"
      },
      "headquarters": {
        "@type": "Place",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Hyderabad",
          "addressRegion": "Telangana",
          "addressCountry": "India"
        }
      },
      "parentOrganization": {
        "@type": "Organization",
        "name": "SP Software (P) Limited"
      },
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "4th Floor, Vaishnavi's Cynosure building, 2-48/5/6, Old Mumbai Hwy, Cyberhills Colony, Telecom Nagar Extension, Gachibowli,",
        "addressLocality": "Hyderabad",
        "addressRegion": " Telangana",
        "postalCode": "500081",
        "addressCountry": "IN"
      },
      "sameAs": [
        "https://www.facebook.com/people/SP-Software-Private-Ltd/100069254829452/",
        "https://twitter.com/SPSoftGlobal",
        "https://www.instagram.com/spsoftglobal_sps/",
        "https://www.youtube.com/@spsoftglobal-company",
        "https://www.linkedin.com/company/spsoftglobal/",
        seoContent.main.url
      ]
    }
    return this.getSafeHTML(localBusinessData);
  }
}